// normalized, as advised in https://redux.js.org/usage/structuring-reducers/normalizing-state-shape

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { addEntityFetchReducers, EntityState } from "./entity";
import { logOut } from "./globalSlice";
import { addSimpleCollectionReducers } from "./simpleCollection";
import { Collection } from "./collection";
import { RootState } from "./store";
import { licenseCollectionThunk, licenseThunk } from "./licensesSlice";
import { Order } from "../api/models";
import { useFetchableState } from "./fetchableState";
import Api from "../api/api";


interface OrdersState extends EntityState<Order> {
    collections: {
        paid: Collection;
        refunded: Collection;
    },
    debug: string
}

/**
 * POST /me/orders/:orderId/refund => replaces orders.entities.orderId.data
 * with the refunded order (the page flips state off the store, no refetch)
 */
export const refundOrderThunk = createAsyncThunk('orders/refundOrder', async ({ id, accessToken }: { id: string, accessToken: string }) => {
    return await Api.refundOrder({ orderId: id, accessToken })
})

const initialState: OrdersState = {
    entities: {},
    collections: {
        paid: { ids: [] },
        refunded: { ids: [] }
    },
    debug: "orders state"
}

export const ordersSlice = createSlice({
    name: 'orders',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            Order,
            { order: Order },
            { id: string, accessToken: string }
        >(
            builder,
            (payload) => payload.order,
            orderFetchThunk)
        addSimpleCollectionReducers<{ accessToken: string }>(
            builder,
            paidOrdersCollectionThunk,
            paidOrdersCollectionSelector,
            paidOrdersCollectionReducerIntercept,
            paidOrdersCollectionRefreshAction
        )
        builder.addCase(logOut, (state: OrdersState) => {
            state = initialState;
        })
        builder.addCase(refundOrderThunk.fulfilled, (state, action) => {
            const { order } = action.payload
            state.entities[order.id] = {
                data: order,
                lastFetched: Date.now()
            }
        })
        builder.addCase(licenseThunk.fulfilled, (state, action) => {
            const { order } = action.payload
            if (order) {
                state.entities[order.id] = {
                    data: order,
                    lastFetched: Date.now()
                }
            }
        })
        builder.addCase(licenseCollectionThunk.fulfilled, (state, action) => {
            const { orders } = action.payload
            orders.forEach(order => {
                state.entities[order.id] = {
                    data: order,
                    lastFetched: Date.now()
                }
            })
        })
    },
})

/**
 * GET /orders/:orderId => orders.entities.orderId.data
 */
const orderFetchThunk = createAsyncThunk('orders/fetchOrder', async ({ id, accessToken }: { id: string, accessToken: string }, { getState, requestId }) => {
    const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
    const entity = state.orders.entities[id];
    if (entity?.currentRequestId !== requestId)
        throw new Error("Request outdated");
    return await Api.getOrder({ orderId: id, accessToken })
})

export function useOrder(id: string, accessToken: string) {
    return useFetchableState<Order>({
        selector: (state: RootState) => state.orders.entities[id],
        action: orderFetchThunk({ id, accessToken })
    })
}

/**
 * GET /me/orders?skuId=[ITEM_ID]&status=paid => orders.entities.collections.paid
 */
const paidOrdersCollectionThunk = createAsyncThunk('orders/fetchPaidOrders', async ({ accessToken }: { accessToken: string }, { getState, requestId }) => {
    const state = getState() as OrdersState;   // avoid sending request if it's going to be discarded anyway
    const { currentRequestId } = state.collections.paid;
    if (currentRequestId !== requestId)
        throw new Error("Request outdated");
    // return await Api.getMeOrders({ status: "paid", accessToken })
})
const paidOrdersCollectionSelector = (state: OrdersState) => state.collections.paid;
const paidOrdersCollectionRefreshAction = { type: "orders/refreshPaidOrders", payload: null } as any;
const paidOrdersCollectionReducerIntercept = (type: any, state: any, action: PayloadAction<any>) => {
    if (type === paidOrdersCollectionThunk.fulfilled) {
        const orders = action.payload.data;
        orders.forEach((order: Order) => {
            const entity = state.entities[order.id];
            if (entity?.currentRequestId !== undefined)
                return;
            state.entities[order.id] = {
                data: order,
                lastFetched: Date.now()
            }
        })
    }
}
export const paidOrdersCollection = ({ accessToken }: { accessToken: string }) => {
    return {
        selector: (state: RootState) => state.orders.collections.paid,
        action: paidOrdersCollectionThunk({ accessToken })
    }
}

export default ordersSlice.reducer